import {
  FlexRowCenterVertical,
  formatErrorMessage,
  message,
  SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY,
} from '@main/core-ui';
import { ImmutableUrl } from '@main/immutable-url';
import { A } from '@main/pc-core-components';
import { endpoints, PAGE_PATHNAMES } from '@main/pc-types';
import {
  StyledPrimaryButton,
  StyledRowContainer,
  StyledSecondaryButton,
} from '@main/privacy-center/src/components';
import { selectRequiredPrivacyCenter } from '@main/privacy-center/src/containers/App/selectors';
import {
  buildUseDhMutation,
  useIsDraftPrivacyCenter,
  useLogout,
} from '@main/privacy-center/src/hooks';
import { sp } from '@main/theme';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { communicationsPreferencesMessages } from '../../messages';

interface EmailPreviewWithUnsubscribeProps {
  /** On button click handler */
  onClick: () => void;
  /** Current state of the unsubscribe all button */
  unsubscribeAll: boolean;
  /** identifier to be displayed */
  identifier?: string;
  /** Show the unsubscribe all button */
  showUnsubscribeAll: boolean;
}

const useGenerateParentPrivacyCenterLoginToken = buildUseDhMutation(
  endpoints.generateParentPrivacyCenterLoginToken,
);

export const EmailPreviewWithUnsubscribe: React.FC<
  EmailPreviewWithUnsubscribeProps
> = ({ onClick, unsubscribeAll, identifier, showUnsubscribeAll }) => {
  const { isDraft } = useIsDraftPrivacyCenter();
  const { parentPrivacyCenter } = useSelector(selectRequiredPrivacyCenter);
  const { formatMessage } = useIntl();
  const logout = useLogout();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [generateParentLoginToken, { error, loading }] =
    useGenerateParentPrivacyCenterLoginToken({});
  useEffect(() => {
    if (error) {
      message.error(formatErrorMessage(error.message));
    }
  }, [error]);

  return (
    <StyledRowContainer>
      <FlexRowCenterVertical style={{ gap: sp(2) }}>
        <div style={{ fontWeight: 600 }}>
          {formatMessage(communicationsPreferencesMessages.emailTitle)}
        </div>
        <p style={{ marginBottom: 0 }}>{identifier}</p>
        <A type="secondary" onClick={logout}>
          {formatMessage(communicationsPreferencesMessages.logOut)}
        </A>
      </FlexRowCenterVertical>
      <FlexRowCenterVertical style={{ gap: sp(2) }}>
        {parentPrivacyCenter && generateParentLoginToken && (
          <StyledPrimaryButton
            buttonType="primary"
            type="button"
            loading={loading || (isRedirecting && !error)}
            onClick={() => {
              setIsRedirecting(true);
              generateParentLoginToken({}).then(({ data }) => {
                if (data) {
                  const url = new ImmutableUrl(
                    parentPrivacyCenter.url,
                  ).transform({
                    pathname: PAGE_PATHNAMES.COMMUNICATIONS_PREFERENCES,
                    searchTransform: (qs) => ({
                      ...qs,
                      modal: 'verify-login-email',
                      token: data.token,
                      redirectPathname:
                        PAGE_PATHNAMES.COMMUNICATIONS_PREFERENCES,
                      ...(isDraft
                        ? { [SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY]: true }
                        : {}),
                    }),
                  });
                  window.location.href = url.href;
                } else {
                  setIsRedirecting(false);
                }
              });
            }}
          >
            {formatMessage(
              communicationsPreferencesMessages.goToParentPrivacyCenter,
            )}
          </StyledPrimaryButton>
        )}
        {showUnsubscribeAll && (
          <StyledSecondaryButton
            buttonType="secondary"
            type="button"
            onClick={onClick}
          >
            {unsubscribeAll
              ? formatMessage(communicationsPreferencesMessages.unsubscribeAll)
              : formatMessage(communicationsPreferencesMessages.subscribeAll)}
          </StyledSecondaryButton>
        )}
      </FlexRowCenterVertical>
    </StyledRowContainer>
  );
};
